import React, { useState, useEffect } from 'react';
import { Modal } from '../Modal/Modal';
import Button from '../Button/Button';
import Toggle from '../Toggle/Toggle';
import { toast } from '../Toast/ToastManager';
import { apiService } from 'src/services';

import './VolunteerSettingsModal.scss';

interface IProps {
  clubId: string;
  isShown: boolean;
  hide: () => void;
  onSave?: () => void;
}

const VolunteerSettingsModal: React.FC<IProps> = ({ clubId, isShown, hide, onSave }) => {
  const [allowLateRegistration, setAllowLateRegistration] = useState<boolean>(false);
  const [allowEarlyRegistration, setAllowEarlyRegistration] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Load settings when modal opens
  useEffect(() => {
    if (isShown && clubId) {
      loadSettings();
    }
  }, [isShown, clubId]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const settings = await apiService.getClubVolunteerSettings(clubId);
      setAllowLateRegistration(settings.allow_late_registration || false);
      setAllowEarlyRegistration(settings.allow_early_registration || false);
    } catch (error) {
      console.error('Error loading volunteer settings:', error);
      toast.show({
        title: 'Error',
        content: 'Failed to load volunteer settings',
        type: 'fail',
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiService.updateClubVolunteerSettings(clubId, {
        allow_late_registration: allowLateRegistration,
        allow_early_registration: allowEarlyRegistration
      });
      
      toast.show({
        title: 'Success',
        content: 'Volunteer settings updated successfully',
        type: 'success',
        duration: 3000
      });

      if (onSave) {
        onSave();
      }
      hide();
    } catch (error) {
      console.error('Error saving volunteer settings:', error);
      toast.show({
        title: 'Error',
        content: 'Failed to save volunteer settings',
        type: 'fail',
        duration: 5000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = () => {
    return (
      <div className="VolunteerSettingsModal">
        <div className="modalHeader">
          <h2>Volunteer Settings</h2>
          <p className="subtitle">These are settings you can enable or disable for volunteers of your club</p>
        </div>
        
        <div className="settingsContent">
          <div className="settingItem">
            <div className="settingInfo">
              <label className="settingLabel">
                Allow volunteers to enter shows/trials before the opening date
              </label>
              <p className="settingDescription">
                When enabled, users with volunteer status can register for events even before the registration opens.
              </p>
            </div>
            <div className="settingControl">
              {isLoading ? (
                <div className="skeletonToggle"></div>
              ) : (
                <Toggle
                  id="allow-early-registration"
                  checked={allowEarlyRegistration}
                  onChange={(checked) => setAllowEarlyRegistration(checked)}
                />
              )}
            </div>
          </div>

          <div className="settingItem">
            <div className="settingInfo">
              <label className="settingLabel">
                Allow volunteers to enter shows/trials after the closing date
              </label>
              <p className="settingDescription">
                When enabled, users with volunteer status can register for events even after the registration closing date.
              </p>
            </div>
            <div className="settingControl">
              {isLoading ? (
                <div className="skeletonToggle"></div>
              ) : (
                <Toggle
                  id="allow-late-registration"
                  checked={allowLateRegistration}
                  onChange={(checked) => setAllowLateRegistration(checked)}
                />
              )}
            </div>
          </div>
        </div>
        
        <div className="modalActions">
          <Button
            variant="secondary"
            onClick={hide}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save Settings
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      className="volunteer-settings-modal"
      isShown={isShown}
      hide={hide}
      modalContent={modalContent()}
    />
  );
};

export default VolunteerSettingsModal;
